import JSZip from "jszip";
import jsPDF from "jspdf";
import { generateJaybeeReport } from "./generateJaybeeReport";
import { buildVqfFormModel } from "./vqfFormLayout";
import { generateFormConfirmationPDF } from "./generateFormConfirmationPDF";

// One-click case-file export (Art. 63 VQF Regulations — electronic documents printable on
// request). Assembles the complete paper-equivalent dossier client-side into a single ZIP:
// compliance report, every client document, per-person Sumsub identity reports, digitised
// Form A/K confirmations, the case-note audit trail, and the screening evidence. Built in
// the browser (not serverless) so document volume is not constrained by function limits.
// The ZIP is a convenience copy — the authoritative record remains the system file (Art. 7).

const REPORT_STATUSES = ["approved", "approved_with_flags", "rejected", "requires_review"];
const safe = (s) => String(s || "").replace(/[^\w.\- ]+/g, "").trim().slice(0, 80) || "file";

const simplePdf = (title, lines) => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const ml = 18; const pw = 210 - ml * 2; let y = 18;
  doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(30, 45, 90);
  doc.text(title, ml, y); y += 8;
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(31, 41, 55);
  lines.forEach((line) => {
    const wrapped = doc.splitTextToSize(String(line || " "), pw);
    wrapped.forEach((w) => {
      if (y > 282) { doc.addPage(); y = 18; }
      doc.text(w, ml, y); y += 4.6;
    });
    y += 1.6;
  });
  return doc.output("blob");
};

export async function exportCaseFile(ctx, onProgress = () => {}) {
  const { supabase, session, report, documents, verifications, caseNotes, sumsubFormRequests,
    walletScreenings, entitySanctions, additionalEntityScreenings, fieldLabel, valueLabel } = ctx;
  const zip = new JSZip();
  const problems = [];

  // ── 01 Compliance report (narrative-backed acceptance report) ─────────────
  onProgress("Generating compliance report…");
  try {
    const res = await fetch("/api/generate-report", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sessionData: { id: session.id, company_name: session.company_name, contact_name: session.contact_name, contact_email: session.contact_email, status: session.status, mlro_decision: session.mlro_decision, mlro_notes: session.mlro_notes, approved_at: session.approved_at, next_review_date: session.next_review_date, report },
        documents: (documents || []).map((d) => ({ document_name: d.document_name, document_type: d.document_type, assessment: d.assessment })),
        caseNotes: (caseNotes || []).slice(0, 15).map((n) => ({ content: n.content, author: n.author, created_at: n.created_at })),
        verifications: (verifications || []).map((v) => ({ person_name: v.person_name, status: v.status, applicant_id: v.applicant_id })),
        walletScreenings: (walletScreenings || []).map((w) => ({ address: w.address, asset: w.asset, risk_level: w.risk_level, risk_score: w.risk_score, cluster_name: w.cluster_name })),
      }),
    });
    const data = await res.json();
    if (data.success && data.narrative) {
      const blob = generateJaybeeReport(session, data.narrative, documents || [], verifications || [], walletScreenings || [], null, true);
      if (blob) zip.file("01 - Compliance Report.pdf", blob);
    } else { problems.push("Compliance report: " + (data.error || "narrative unavailable")); }
  } catch (e) { problems.push("Compliance report: " + e.message); }

  // ── 02 Client documents ────────────────────────────────────────────────────
  for (let i = 0; i < (documents || []).length; i++) {
    const d = documents[i];
    if (!d.file_path) continue;
    onProgress(`Fetching document ${i + 1} of ${documents.length}…`);
    try {
      const { data, error } = await supabase.storage.from("onboarding-documents").download(d.file_path);
      if (error) throw new Error(error.message);
      const ext = (d.file_path.split(".").pop() || "pdf").slice(0, 5);
      const cls = safe(d.document_type || "Document");
      const nm = safe(d.document_name || "file");
      zip.file(`02 - Client Documents/${cls} - ${nm}${nm.toLowerCase().endsWith("." + ext.toLowerCase()) ? "" : "." + ext}`, data);
    } catch (e) { problems.push(`Document "${d.document_name}": ${e.message}`); }
  }

  // ── 03 Identity reports (per-person Sumsub PDFs) ───────────────────────────
  const reportable = (verifications || []).filter((v) => REPORT_STATUSES.includes(v.status));
  for (let i = 0; i < reportable.length; i++) {
    const v = reportable[i];
    onProgress(`Fetching identity report ${i + 1} of ${reportable.length} (${v.person_name})…`);
    try {
      let path = v.report_path;
      if (!path) {
        const r = await fetch("/api/sumsub-report", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ sessionId: v.session_id || session.id, applicantId: v.applicant_id, externalUserId: v.external_user_id }) });
        const d = await r.json();
        if (!d.success) throw new Error(d.error || "report unavailable");
        path = d.path;
      }
      const { data, error } = await supabase.storage.from("onboarding-documents").download(path);
      if (error) throw new Error(error.message);
      zip.file(`03 - Identity Reports/${safe(v.person_name)} - Sumsub Identity Report.pdf`, data);
    } catch (e) { problems.push(`Identity report ${v.person_name}: ${e.message}`); }
  }

  // ── 04 Form A/K confirmations (digitised VQF forms) ────────────────────────
  const confirmed = (sumsubFormRequests || []).filter((r) => (r.form_type === "A" || r.form_type === "K") && r.status === "confirmed");
  for (let i = 0; i < confirmed.length; i++) {
    const rec = confirmed[i];
    onProgress(`Generating Form ${rec.form_type} confirmation ${i + 1} of ${confirmed.length}…`);
    try {
      const params = new URLSearchParams({ action: "form-answers", applicantId: rec.applicant_id || "" });
      if (rec.applicant_action_id) params.set("applicantActionId", rec.applicant_action_id);
      const r = await fetch(`/api/sumsub?${params.toString()}`);
      const d = await r.json();
      const model = buildVqfFormModel({ record: rec, questionnaire: d.success && d.found ? d.questionnaire : { sections: {} }, session, report, fieldLabel, valueLabel });
      const blob = generateFormConfirmationPDF(model, { returnBlob: true });
      zip.file(`04 - Form Confirmations/Form ${rec.form_type} - ${safe(rec.signer_name)}.pdf`, blob);
    } catch (e) { problems.push(`Form ${rec.form_type} (${rec.signer_name}): ${e.message}`); }
  }

  // ── 05 Case notes (audit trail) ────────────────────────────────────────────
  onProgress("Compiling case notes…");
  const noteLines = (caseNotes || []).length
    ? (caseNotes || []).map((n) => `${n.created_at ? new Date(n.created_at).toLocaleString("en-GB") : ""} — ${n.author || ""}\n${n.content || ""}\n`)
    : ["No case notes on file."];
  zip.file("05 - Case Notes.pdf", simplePdf(`Case Notes — ${session.company_name || ""}`, noteLines));

  // ── 06 Screening evidence ──────────────────────────────────────────────────
  onProgress("Compiling screening evidence…");
  const scr = [];
  if (entitySanctions) {
    const d = entitySanctions.data || {};
    scr.push(`ENTITY SANCTIONS SCREENING — OpenSanctions`);
    scr.push(`Entity: ${d.entityChecked || session.company_name || ""} · Result: ${entitySanctions.status} · ${d.checkedAt ? new Date(d.checkedAt).toLocaleString("en-GB") : ""}`);
    if (d.listsSearched) scr.push(`Lists: ${d.listsSearched}`);
    const dism = d.dismissed || {};
    [...(d.hits || []), ...(d.cautionMatches || [])].forEach((h) => {
      scr.push(`Match: ${h.name} (${h.score}%) — ${(h.datasets || []).join(", ")}${dism[h.name] ? ` — FALSE POSITIVE: ${dism[h.name].reason} (${dism[h.name].dismissedBy}, ${new Date(dism[h.name].dismissedAt).toLocaleDateString("en-GB")})` : " — UNRESOLVED"}`);
    });
    scr.push("");
  } else { scr.push("ENTITY SANCTIONS SCREENING — not yet run", ""); }
  (additionalEntityScreenings || []).forEach((a) => {
    scr.push(`ADDITIONAL ENTITY: ${a.name} · Result: ${a.status} · ${a.checkedAt ? new Date(a.checkedAt).toLocaleString("en-GB") : ""}`);
    const dism = (a.data && a.data.dismissed) || {};
    [...((a.data && a.data.hits) || []), ...((a.data && a.data.cautionMatches) || [])].forEach((h) => {
      scr.push(`  Match: ${h.name} (${h.score}%)${dism[h.name] ? ` — FALSE POSITIVE: ${dism[h.name].reason} (${dism[h.name].dismissedBy})` : " — UNRESOLVED"}`);
    });
  });
  if ((walletScreenings || []).length) {
    scr.push("", "WALLET SCREENINGS — Chainalysis");
    walletScreenings.forEach((w) => scr.push(`${w.address} (${w.asset}) — Risk: ${w.risk_level}, Score: ${w.risk_score}/10${w.cluster_name ? `, Cluster: ${w.cluster_name}` : ""}`));
  }
  zip.file("06 - Screening/Screening Evidence.pdf", simplePdf(`Screening Evidence — ${session.company_name || ""}`, scr));

  if (problems.length) zip.file("EXPORT-NOTES.txt", "Items that could not be included:\n\n" + problems.join("\n"));

  onProgress("Building ZIP…");
  const blob = await zip.generateAsync({ type: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `SCM-CaseFile-${safe(session.company_name || "Client").replace(/\s+/g, "-")}-${new Date().toISOString().slice(0, 10)}.zip`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 10000);
  onProgress("");
  return { problems };
}
